import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuCheck, LuCopy } from 'react-icons/lu';

import { MCP_BASE_URL } from '../../shared/api';

const MCP_SERVER_URL = `${MCP_BASE_URL}/mcp`;

type Client = 'code' | 'desktop' | 'web';

const CLIENTS: { id: Client; label: string }[] = [
  { id: 'code', label: 'Claude Code' },
  { id: 'desktop', label: 'Claude Desktop' },
  { id: 'web', label: 'claude.ai' },
];

const McpIntegration: React.FC = () => {
  const [client, setClient] = useState<Client>('code');
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(MCP_SERVER_URL);
      setCopied(true);
      toast.success('Server URL copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <div className="space-y-3">
      <div className="text-sm opacity-70">
        Connect Claude directly to your mFlow account via the Model Context Protocol — ask about your projects, create
        tasks, or check delivery risk without leaving your chat.
      </div>

      <div className="flex items-center gap-2">
        <code className="flex-1 min-w-0 truncate rounded-lg bg-base-200 px-3 py-2 text-xs font-mono">
          {MCP_SERVER_URL}
        </code>
        <button className="btn btn-sm btn-outline gap-1 shrink-0" onClick={handleCopy}>
          {copied ? <LuCheck className="text-success" /> : <LuCopy />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      <div role="tablist" className="tabs tabs-boxed w-fit">
        {CLIENTS.map((c) => (
          <button
            key={c.id}
            role="tab"
            className={`tab ${client === c.id ? 'tab-active' : ''}`}
            onClick={() => setClient(c.id)}
          >
            {c.label}
          </button>
        ))}
      </div>

      {client === 'code' && (
        <ol className="list-decimal list-inside text-sm space-y-1.5 opacity-80">
          <li>
            Run{' '}
            <code className="text-xs font-mono bg-base-200 rounded px-1.5 py-0.5">
              claude mcp add --transport http mflow {MCP_SERVER_URL}
            </code>
          </li>
          <li>
            Start a session and run <code className="text-xs font-mono bg-base-200 rounded px-1.5 py-0.5">/mcp</code>,
            select <b>mflow</b>, then choose <b>Authenticate</b>
          </li>
          <li>Sign in and allow access in the browser tab that opens</li>
          <li>
            Back in Claude Code, <b>mflow</b> shows as connected
          </li>
        </ol>
      )}

      {client === 'desktop' && (
        <ol className="list-decimal list-inside text-sm space-y-1.5 opacity-80">
          <li>
            Open <b>Settings → Connectors → Add custom connector</b>
          </li>
          <li>
            Paste the server URL above and click <b>Add</b>
          </li>
          <li>A browser window opens automatically — sign in and allow access</li>
          <li>The connector is now available in every Claude Desktop chat</li>
        </ol>
      )}

      {client === 'web' && (
        <ol className="list-decimal list-inside text-sm space-y-1.5 opacity-80">
          <li>
            Open <b>Customize → Connectors → Add custom connector</b>
          </li>
          <li>
            Paste the server URL above and click <b>Add</b>
          </li>
          <li>A browser tab opens automatically — sign in and allow access</li>
          <li>The connector syncs to claude.ai, Desktop, and mobile</li>
        </ol>
      )}

      <div className="text-xs opacity-50">
        No API key or client secret needed — mFlow&apos;s server handles sign-in via your existing mFlow account.
      </div>
    </div>
  );
};

export default McpIntegration;
